import React, { Component, ErrorInfo, ReactNode } from "react"
import { captureError } from "../../ErrorHandlers"
import { Button } from "@blueprintjs/core"
import { Trans } from "react-i18next"
import i18n from "@/lib/i18n/i18n"

interface Props {
  children: ReactNode
  fallbackMessage?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

class MapErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error: error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    window.analytics.track("MapCrash", { error })
    captureError(error, { origin: "Map" })
  }

  private handleReload = () => {
    window.location.reload()
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallbackMessage) {
        return this.props.fallbackMessage
      }

      return (
        <div className="box-border flex h-full w-full flex-col items-center justify-center border border-dashed border-gray-300 bg-gray-100 p-5 text-center text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          <p>{i18n.t("maps.mapErrorBoundary.problem")}</p>
          <p>
            <Trans
              i18nKey="maps.mapErrorBoundary.reloadHint"
              components={{ b: <strong /> }}
            />
          </p>
          <Button
            onClick={this.handleReload}
            className="mt-4 cursor-pointer px-4 py-2"
          >
            {" "}
            {i18n.t("maps.mapErrorBoundary.reloadPage")}
          </Button>
          {import.meta.env.MODE === "development" && this.state.error && (
            <pre className="mt-2.5 max-w-full overflow-auto text-left text-xs text-red-500">
              {this.state.error.toString()}
            </pre>
          )}
        </div>
      )
    }

    return this.props.children
  }
}

export default MapErrorBoundary
